import { useEditor, EditorContent } from '@tiptap/react'
import classNames from 'classnames'
import s from './index.module.css'
import { useOutside } from '../../../hooks'
import SelectColor from '../SelectColor'
import { useEffect, useRef, useState } from 'react'
import './styles.css'
import  { useCallback } from 'react'
import { getTiptapExtensions } from './tiptap-extensions'
import { selectProfile } from '@store/profile.reducer'
import { useDispatch, useSelector } from 'react-redux'
import { uploadImageToServer } from './utils/uploadImageToServer'
import dayjs from '@store/timetable/dayjs.config'
import { onvertingRelativeURLsToAbsoluteOnes } from '@UI/JsonPars/helpers/onvertingRelativeURLsToAbsoluteOnes'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import { useAddAlert } from '@/hooks/useAddAlert'
import { closeModalSelectFiles, openModal } from '@store/modalsSlise'
import TYPES from '@modals/TYPES'
import { AlignCenterIcon, AlignLeftIcon, AlignRightIcon } from '@/utils/icons'
import useRoles from '@/hooks/useRoles'

const MenuBar = ({ className, editor, isUploading, style, applyColorToAllOnEmptySelection = false }) => {
  const [color, setColor] = useState('#000000')
  const [ref, isShow, setIsShow] = useOutside(false)
  const colorSelectionRef = useRef(null)
  const { isChildrenOrParent } = useRoles()

  const dispatch = useDispatch();

  const rememberColorSelection = () => {
    if (!editor) {
      return
    }

    const { from, to } = editor.state.selection
    colorSelectionRef.current = { from, to }
  }
  
  const openModalSelectFile = () => {
    dispatch(openModal({
      type: TYPES.SELECT_FILES_FROM_MEDIA_PLATFORM,
      data: {
          type: "Изображение",
          onSuccess: (file) => {
              editor.chain()
              .focus()
              .setImage({ 
                src: getCorrectFilePath(file?.path),
                alt: file?.name ?? '',
                title: file?.name ?? ''
              })
              .run();
              dispatch(closeModalSelectFiles())
          }
      }
    }))
  }
  
  const onSetColor = color => {
    const colorVal = (color?.target?.value) ? (color?.target?.value) : color
    const savedSelection = colorSelectionRef.current
    let command = editor.chain().focus()

    if (savedSelection?.from !== savedSelection?.to) {
      command = command.setTextSelection(savedSelection)
    } else if (applyColorToAllOnEmptySelection && !editor.isEmpty) {
      command = command.selectAll()
    }

    command.setColor(colorVal).run()
    setColor(colorVal)
    setIsShow(false)
  }

  const setLink = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href
    const url = window.prompt('Введите ссылку', previousUrl)

    if (url === null) {
      return
    }
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink()
        .run()
      return
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url })
      .run()
  }, [editor])

  if (!editor) return null
  
  const action = type => {
    switch (type) {
      case 'bold': editor.chain().focus().toggleBold().run()
        break
      case 'italic': editor.chain().focus().toggleItalic().run()
        break
      case 'underline': editor.chain().focus().toggleUnderline().run()
        break
      case 'strike': editor.chain().focus().toggleStrike().run()
        break
      case 'bulletList': editor.chain().focus().toggleBulletList().run()
        break
      case 'orderedList': editor.chain().focus().toggleOrderedList().run()
        break
      case 'textAlignLeft': editor.chain().focus().setTextAlign('left').run()
        break
      case 'textAlignCenter': editor.chain().focus().setTextAlign('center').run()
        break
      case 'textAlignRight': editor.chain().focus().setTextAlign('right').run()
        break
      case 'math': editor.chain().insertContent('').toggleMath().insertContent(' ').run()
      // case 'math': editor.chain().insertContent(' ').toggleMath().insertContent(' ').run() //самый первый вариант, который всегда работал
      // case 'math': editor.chain().focus().toggleMath().run();
        break
      default: break
    }
  }

  const onMenuMouseDown = (event) => {
    rememberColorSelection()

    if (event.target.closest('input')) {
      return
    }

    event.preventDefault()
  }

  const onColorMouseDown = (event) => {
    rememberColorSelection()
    event.preventDefault()
    event.stopPropagation()
    setIsShow(true)
  }


  return <div
    className={classNames(s.menuWrap, className)}
    style={style}
    onMouseDown={onMenuMouseDown}
  >
    <div className={s.menu}>

      <button onClick={() => action('bold')} className={classNames(s.btn, { [s.active]: editor.isActive('bold') })} >
        <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <g>
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M8 11h4.5a2.5 2.5 0 1 0 0-5H8v5zm10 4.5a4.5 4.5 0 0 1-4.5 4.5H6V4h6.5a4.5 4.5 0 0 1 3.256 7.606A4.498 4.498 0 0 1 18 15.5zM8 13v5h5.5a2.5 2.5 0 1 0 0-5H8z"></path>
          </g>
        </svg>
      </button>

      <button onClick={() => action('italic')} className={classNames(s.btn, { [s.active]: editor.isActive('italic') })} >
        <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <g>
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M15 20H7v-2h2.927l2.116-12H9V4h8v2h-2.927l-2.116 12H15z"></path>
          </g>
        </svg>
      </button>

      <button onClick={() => action('underline')} className={classNames(s.btn, { [s.active]: editor.isActive('underline') })}>
        <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <g>
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M8 3v9a4 4 0 1 0 8 0V3h2v9a6 6 0 1 1-12 0V3h2zM4 20h16v2H4v-2z"></path>
          </g>
        </svg>
      </button>

      <button onClick={() => action('strike')} className={classNames(s.btn, { [s.active]: editor.isActive('strike') })}>
        <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <g>
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M17.154 14c.23.516.346 1.09.346 1.72 0 1.342-.524 2.392-1.571 3.147C14.88 19.622 13.433 20 11.586 20c-1.64 0-3.263-.381-4.87-1.144V16.6c1.52.877 3.075 1.316 4.666 1.316 2.551 0 3.83-.732 3.839-2.197a2.21 2.21 0 0 0-.648-1.603l-.12-.117H3v-2h18v2h-3.846zm-4.078-3H7.629a4.086 4.086 0 0 1-.481-.522C6.716 9.92 6.5 9.246 6.5 8.452c0-1.236.466-2.287 1.397-3.153C8.83 4.433 10.271 4 12.222 4c1.471 0 2.879.328 4.222.984v2.152c-1.2-.687-2.515-1.03-3.946-1.03-2.48 0-3.719.782-3.719 2.346 0 .42.218.786.654 1.099.436.313.974.562 1.613.75.62.18 1.297.414 2.03.699z"></path>
          </g>
        </svg>
      </button>

      {/* Кнопка для вставки изображения */}
      {!isChildrenOrParent && (
        <button
          onClick={openModalSelectFile}
          disabled={isUploading}
          title="Добавить изображение"
        >
          {isUploading ? (
            <div className={s.uploadSpinner}>⏳</div>
          ) : (
            <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
              <path d="M5 11.1l2-2 5.5 5.5 3.5-3.5 3 3V5H5v6.1zm0 2.829V19h3.1l2.986-2.985L7 11.929l-2 2zM10.929 19H19v-2.071l-3-3L10.929 19zM4 3h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1zm11.5 7a1.5 1.5 0 110-3 1.5 1.5 0 010 3z"/>
            </svg>
          )}
        </button>
      )}

      <div ref={ref} className={s.colorWrap}>
        <span style={{ backgroundColor: color }} onMouseDown={onColorMouseDown} className={s.color}></span>
        <div
          className={classNames(s.colorContainer, { [s.show]: isShow })}
          onMouseDown={(event) => {
            rememberColorSelection()
            event.preventDefault()
            event.stopPropagation()
          }}
        >
          <SelectColor value={color} onClick={onSetColor} className={s.colorSelect} type='code' />
        </div>
      </div>

      <label className={classNames(s.btn)}>
        <svg className={s.ico} xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <path d="M5 5v3h14V5H5zM4 3h16a1 1 0 0 1 1 1v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1zm2 9h6a1 1 0 0 1 1 1v3h1v6h-4v-6h1v-2H5a1 1 0 0 1-1-1v-2h2v1zm11.732 1.732 1.768-1.768 1.768 1.768a2.5 2.5 0 1 1-3.536 0z" />
        </svg>
        <input type="color" className={s.colorI} onMouseDown={rememberColorSelection} onInput={onSetColor} value={editor.getAttributes('textStyle').color ? editor.getAttributes('textStyle').color : ''} />
      </label>



      <button onClick={() => action('bulletList')} className={classNames(s.btn, { [s.active]: editor.isActive('bulletList') })} >
        <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <g>
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M8 4h13v2H8V4zM4.5 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0 7a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm0 6.9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zM8 11h13v2H8v-2zm0 7h13v2H8v-2z"></path>
          </g>
        </svg>
      </button>

      <button onClick={() => action('orderedList')} className={classNames(s.btn, { [s.active]: editor.isActive('orderedList') })} >
        <svg className={s.ico} width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
          <g>
            <path fill="none" d="M0 0h24v24H0z"></path>
            <path d="M8 4h13v2H8V4zM5 3v3h1v1H3V6h1V4H3V3h2zM3 14v-2.5h2V11H3v-1h3v2.5H4v.5h2v1H3zm2 5.5H3v-1h2V18H3v-1h3v4H3v-1h2v-.5zM8 11h13v2H8v-2zm0 7h13v2H8v-2z"></path>
          </g>
        </svg>
      </button>

      <button
        onClick={() => action('textAlignLeft')}
        className={classNames(s.btn, { [s.active]: editor.isActive({ textAlign: 'left' }) })}
        title="Выравнивание по левому краю"
      >
        <AlignLeftIcon className={s.ico} width="24" height="24" />
      </button>

      <button
        onClick={() => action('textAlignCenter')}
        className={classNames(s.btn, { [s.active]: editor.isActive({ textAlign: 'center' }) })}
        title="Выравнивание по центру"
      >
        <AlignCenterIcon className={s.ico} width="24" height="24" />
      </button>

      <button
        onClick={() => action('textAlignRight')}
        className={classNames(s.btn, { [s.active]: editor.isActive({ textAlign: 'right' }) })}
        title="Выравнивание по правому краю"
      >
        <AlignRightIcon className={s.ico} width="24" height="24" />
      </button>

      <button onClick={() => action('math')} className={classNames(s.btn, { [s.active]: editor.isActive('math') })} >
        <svg className={s.ico} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.3125 13.5H18.75L17.1875 16.4227L15.625 13.5H14.0625L16.332 17.25L14.0625 21H15.625L17.1875 18.1508L18.75 21H20.3125L18.0453 17.25L20.3125 13.5Z" />
          <path d="M14.8437 4.5V3H10.8698C10.4794 2.99836 10.1026 3.13773 9.81432 3.39044C9.52603 3.64315 9.34728 3.99072 9.31359 4.3641L8.80359 9.75H5.46875V11.25H8.66148L7.88023 19.5H3.90625V21H7.88023C8.27063 21.0016 8.64738 20.8623 8.93568 20.6096C9.22397 20.3568 9.40272 20.0093 9.43641 19.6359L10.2306 11.25H14.0625V9.75H10.3726L10.8698 4.5H14.8437Z" />
        </svg>
      </button>
      <div className={s.link}>
        <button onClick={setLink}>
          Создать ссылку
        </button>
        <button
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
        >
          Сбросить
        </button>
      </div>
    </div>
  </div>
}

const getTiptapExtensionsAll = additionalExtention => {
  if (additionalExtention)
    return getTiptapExtensions().concat(additionalExtention);
  return getTiptapExtensions();
}

const getMathNodeValue = (item) => {
  if (typeof item?.attrs?.value === 'string' && item.attrs.value.length > 0) {
    return item.attrs.value
  }

  return item?.content?.map((child) => child?.text || '').join('') || ''
}

const normalizeMathNode = (item) => ({
  type: 'math',
  attrs: {
    ...item?.attrs,
    value: getMathNodeValue(item),
  },
})

const getMathTrailingTextNode = (item) => {
  const trailingText = item?.content?.map((child) => child?.text || '').join('') || ''

  if (!trailingText.trim()) {
    return null
  }

  return {
    type: 'text',
    text: trailingText,
  }
}

const normalizeMathNodeWithTrailingText = (item) => {
  const mathNode = normalizeMathNode(item)
  const trailingTextNode = getMathTrailingTextNode(item)

  return trailingTextNode ? [mathNode, trailingTextNode] : [mathNode]
}

const normalizeMathContentForEditor = (content = []) =>
  content.flatMap((item) => {
    if (!item || typeof item !== 'object') {
      return [item]
    }

    if (item.type === 'math') {
      return [{
        type: 'paragraph',
        attrs: {
          textAlign: null,
        },
        content: normalizeMathNodeWithTrailingText(item),
      }]
    }

    if (Array.isArray(item.content)) {
      return [{
        ...item,
        content: item.content.flatMap((child) => {
          if (child?.type === 'math') {
            return normalizeMathNodeWithTrailingText(child)
          }

          if (Array.isArray(child?.content)) {
            return [{
              ...child,
              content: normalizeMathContentForEditor(child.content),
            }]
          }

          return [child]
        }),
      }]
    }

    return [item]
  })

const normalizeMathDocForSave = (node) => {
  if (!node || typeof node !== 'object') {
    return node
  }

  if (node.type === 'math') {
    return normalizeMathNode(node)
  }

  if (Array.isArray(node.content)) {
    return {
      ...node,
      content: node.content.flatMap((child) => {
        if (child?.type === 'math') {
          return normalizeMathNodeWithTrailingText(child)
        }

        return [normalizeMathDocForSave(child)]
      }),
    }
  }

  return node
}

const Tiptap = ({ placeholder = '', value = '',
  error,
  setValue = () => { },
  tiptapClassWrapper = '',
  tiptapClassContent = '',
  tiptapClassContentProseMirror = '',
  updateMarker = false,
  style = {},
  styleEditor = {},
  hideMenuBar = false,
  inlineMenuBar = false,
  floatingMenu = false,
  getEditor = () => {},
  additionalExtention = null, // расширение или массив расширений
  ...props }) => {
  const [stateValue, setStateValue] = useState(value);
  const [flag, reRenderEditor] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const profile = useSelector(selectProfile);
  const addAlert = useAddAlert()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!value?.type || value?.content !== stateValue?.content) {
      reRenderEditor(!flag)
    }
  }, [value]);

  const editor = useEditor({
    editable: !props?.disabled,
    extensions: getTiptapExtensionsAll(additionalExtention),
    content: (() => {
      if (typeof value === 'object') {
        if (Array.isArray(value?.content)) {
          return {
            ...value,
            content: normalizeMathContentForEditor(
              onvertingRelativeURLsToAbsoluteOnes(value.content),
            ),
          };
        }
        return value;
      }
      return String(value);
    })(),
    onUpdate: ({ editor }) => {
      const json = normalizeMathDocForSave(editor.getJSON())
      setStateValue(json)
      setValue(json)
    },
  }, [flag, updateMarker, props?.disabled]);

  useEffect(() => {
    if (editor && tiptapClassContentProseMirror) {
        const proseMirrorElement = editor.view.dom;
        proseMirrorElement.classList.add(tiptapClassContentProseMirror); // Добавляем ваш класс
    }
    getEditor(editor);
  }, [editor]);

   
  // Общая функция для загрузки изображения
  const uploadImage = useCallback(async (imageFile) => {
    if (!imageFile || !imageFile?.size || !editor || isUploading) return false;

    const fileName = `изображение из текстового редактора ${dayjs().format('MM.DD.YYYY HH:mm:ss')}`;
    setIsUploading(true);

    try {
      const imageUrl = await uploadImageToServer({
        file: imageFile,
        fileName,
        profileId: profile?.id
      });
      
      editor.chain()
        .focus()
        .setImage({ 
          src: getCorrectFilePath(imageUrl?.path),
          alt: fileName,
          title: fileName
        })
        .run();
      
      addAlert({
        title: 'Изображение успешно загружено', 
        text: 'Для изменения размера кликните по изображению'
      });
      return true;
    } catch (error) {
      addAlert({
        type: 'error', 
        title: 'Ошибка', 
        text: 'Не удалось загрузить изображение'
      });
      return false;
    } finally {
      setIsUploading(false);
    }
  }, [editor, uploadImageToServer, isUploading]);

  // Обработчик вставки изображения
  const handlePaste = useCallback(async (event) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    // Находим изображение
    let imageFile = null;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        imageFile = item.getAsFile();
        if (imageFile) break;
      }
    }

    if (!imageFile || !imageFile?.size) return;

    event.preventDefault();
    event.stopPropagation();
    
    await uploadImage(imageFile);
  }, [uploadImage]);

  const [ref, show, setIsShow] = useOutside(false)
  const [selectionMenuStyle, setSelectionMenuStyle] = useState(null)
  const [floatingMenuStyle, setFloatingMenuStyle] = useState(null)
  const [isSelectionEmpty, setIsSelectionEmpty] = useState(true)

  const onHove = () => setIsShow(true)

  useEffect(() => {
    if (!editor) {
      return
    }

    const updateSelectionState = () => {
      const selectionIsEmpty = editor.state.selection.empty
      setIsSelectionEmpty(selectionIsEmpty)

      if (selectionIsEmpty || !editor.view.hasFocus()) {
        setSelectionMenuStyle(null)
        return
      }

      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0) {
        setSelectionMenuStyle(null)
        return
      }

      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()

      if (!rect || (!rect.width && !rect.height)) {
        setSelectionMenuStyle(null)
        return
      }

      const menuHalfWidth = window.innerWidth <= 767 ? 128 : 150
      const left = Math.min(
        Math.max(rect.left + rect.width / 2, 8 + menuHalfWidth),
        window.innerWidth - 8 - menuHalfWidth,
      )

      const placeAbove = rect.top >= 100 || window.innerHeight - rect.bottom < 100
      setSelectionMenuStyle({
        position: 'fixed',
        top: placeAbove ? rect.top - 12 : rect.bottom + 12,
        left,
        transform: placeAbove ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
        zIndex: 1100,
      })
    }

    updateSelectionState()

    editor.on('selectionUpdate', updateSelectionState)
    editor.on('focus', updateSelectionState)
    editor.on('blur', updateSelectionState)
    window.addEventListener('resize', updateSelectionState)
    window.addEventListener('scroll', updateSelectionState, true)

    return () => {
      editor.off('selectionUpdate', updateSelectionState)
      editor.off('focus', updateSelectionState)
      editor.off('blur', updateSelectionState)
      window.removeEventListener('resize', updateSelectionState)
      window.removeEventListener('scroll', updateSelectionState, true)
    }
  }, [editor])

  useEffect(() => {
    if (!floatingMenu || !show || !isSelectionEmpty) {
      setFloatingMenuStyle(null)
      return
    }

    const updateMenuPosition = () => {
      const rect = ref.current?.getBoundingClientRect?.()
      if (!rect) return

      const menuWidth = Math.min(320, window.innerWidth - 16)
      const menuHeight = 94
      const placeAbove = rect.top >= menuHeight + 8 ||
        window.innerHeight - rect.bottom < menuHeight + 8
      const left = Math.min(
        Math.max(rect.left, 8),
        Math.max(8, window.innerWidth - menuWidth - 8),
      )

      setFloatingMenuStyle({
        position: 'fixed',
        top: placeAbove ? rect.top - 4 : rect.bottom + 4,
        left,
        transform: placeAbove ? 'translateY(-100%)' : 'none',
        zIndex: 1100,
      })
    }

    updateMenuPosition()
    window.addEventListener('resize', updateMenuPosition)
    window.addEventListener('scroll', updateMenuPosition, true)

    return () => {
      window.removeEventListener('resize', updateMenuPosition)
      window.removeEventListener('scroll', updateMenuPosition, true)
    }
  }, [floatingMenu, show, isSelectionEmpty, ref])

  const condition = () => {
    return editor ? editor.isEmpty : true;
  }

  return <div ref={ref} onFocus={onHove}
    className={classNames(s.wrapper, 'wrapperTiptapLibrary', 'tiptapStyle', {[tiptapClassWrapper]: tiptapClassWrapper})}
    placeholder={condition() ? placeholder : ''}
    style={style}
    onPaste={handlePaste}
  >
    {isUploading && <div className={s.isUploading}>Загрузка изображения...</div> }
    {
      !hideMenuBar && (
        <MenuBar 
          onHove={onHove} 
          isUploading={isUploading}
          editor={editor} 
          style={inlineMenuBar
            ? undefined
            : selectionMenuStyle || floatingMenuStyle || undefined}
          applyColorToAllOnEmptySelection={props?.applyColorToAllOnEmptySelection}
          className={classNames(
            { [s.menuWrapInline]: inlineMenuBar },
            !inlineMenuBar && (
              selectionMenuStyle
                ? s.menuWrapSelection
                : { [s.show]: show && isSelectionEmpty }
            ),
          )} />
      )
    }
    <EditorContent editor={editor}
                   placeholder={condition() ? placeholder : ''}
                   className={classNames(s.text, 'wrapperTiptapLibraryText', {[tiptapClassContent]: tiptapClassContent}, { [s.error]: error })}
                  style={styleEditor}
    />
  </div>
}

export default Tiptap
